import { Component, Inject } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import {
  CountriesService,
  SimpleCountry,
} from "src/app/_services/countries.service";
import {
  AVAILABLE_LOCALES,
  AvailableLocale,
} from "src/app/_static-data/available-locale.library";
import {
  Contact,
  Exhibition,
  ExhibitionTranslation,
  ExhibitionWithRelations,
  NewExhibition,
} from "src/app/api/models";
import { TinyService } from "src/app/shared/services/tiny.service";

export interface ExhibitionCreateUpdateDialogData {
  context: "editor" | "inventory" | "item";
  exhibition?: ExhibitionWithRelations;
  contacts: Contact[];
}

@Component({
  selector: "app-exhibition-create-update-dialog",
  templateUrl: "./exhibition-create-update-dialog.component.html",
  styleUrl: "./exhibition-create-update-dialog.component.scss",
  standalone: false,
})
export class ExhibitionCreateUpdateDialogComponent {
  availableLocales: AvailableLocale[] = AVAILABLE_LOCALES;
  TITLE_CONTROL_NAMES: string[] = ["originalTitle", "titleCS", "titleEN"];

  countries: SimpleCountry[] = [];
  form: UntypedFormGroup;
  mode: "create" | "update" = "create";
  context: "editor" | "inventory" | "item";
  exhibition: ExhibitionWithRelations;
  contacts: Contact[] = [];

  originalTitleLocaleSubscription: Subscription = new Subscription();
  titleEnSubscription: Subscription = new Subscription();
  titleCsSubscription: Subscription = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) public defaults: ExhibitionCreateUpdateDialogData,
    private dialogRef: MatDialogRef<ExhibitionCreateUpdateDialogComponent>,
    private fb: UntypedFormBuilder,
    private countriesService: CountriesService,
    public tinyService: TinyService
  ) {}

  ngOnDestroy(): void {
    this.originalTitleLocaleSubscription.unsubscribe();
    this.titleEnSubscription.unsubscribe();
    this.titleCsSubscription.unsubscribe();
  }

  async ngOnInit(): Promise<void> {
    this.countries = this.countriesService.getSimpleCountryList();
    this.contacts = this.defaults.contacts;
    this.context = this.defaults.context;
    if (this.defaults.exhibition) {
      this.mode = "update";
      this.exhibition = this.defaults.exhibition;
    } else {
      this.exhibition = {} as ExhibitionWithRelations;
    }
    this.prepareForm();
    if (!this.canEdit()) {
      this.form.disable({ emitEvent: false });
    }
  }

  /** Default submit function */
  save() {
    if (this.mode === "create") {
      this.create();
    } else if (this.mode === "update") {
      this.update();
    }
  }

  /** CRUD Functions */
  private create() {
    const ne: NewExhibition = {
      exhibition: {
        originalTitle: this.getLocalizedOriginalTitle(),
        organizerId: this.form.value["organizerId"],
        year: this.form.value["year"],
        countryCode: this.form.value["countryCode"],
        curated: this.form.value["curated"],
      },
      translations: [
        {
          localeId: 1,
          title: this.form.value["titleCS"],
          isOriginalTitle:
            this.form.value["originalTitleLocale"] === "cs" ? true : false,
          year: this.form.value["yearCS"],
        },
        {
          localeId: 2,
          title: this.form.value["titleEN"],
          isOriginalTitle:
            this.form.value["originalTitleLocale"] === "en" ? true : false,
          year: this.form.value["yearEN"],
        },
      ],
    };
    this.dialogRef.close(ne);
  }

  private update() {
    const tCS = this.exhibition.translations?.find((a) => a.localeId === 1);
    const tEN = this.exhibition.translations?.find((a) => a.localeId === 2);

    const exhibition: Partial<Exhibition> = {
      id: this.exhibition.id,
      originalTitle: this.getLocalizedOriginalTitle(),
      organizerId: this.form.value["organizerId"],
      year: this.form.value["year"],
      countryCode: this.form.value["countryCode"],
      curated: this.form.value["curated"],
    };

    const translationCS: ExhibitionTranslation = {
      id: tCS.id,
      localeId: 1,
      title: this.form.value["titleCS"],
      cityName: this.form.value["cityNameCS"],
      description: this.form.value["descriptionCS"],
      year: this.form.value["yearCS"],
    };

    //Updates info about original title locale if was changed
    if (
      this.form.value["originalTitleLocale"] !== "cs" &&
      tCS.isOriginalTitle === true
    ) {
      translationCS.isOriginalTitle = false;
    } else if (
      this.form.value["originalTitleLocale"] === "cs" &&
      tCS.isOriginalTitle === false
    ) {
      translationCS.isOriginalTitle = true;
    }

    const translationEN: ExhibitionTranslation = {
      id: tEN.id,
      localeId: 2,
      title: this.form.value["titleEN"],
      cityName: this.form.value["cityNameEN"],
      description: this.form.value["descriptionEN"],
      year: this.form.value["yearEN"],
    };

    //Updates info about original title locale if was changed
    if (
      this.form.value["originalTitleLocale"] !== "en" &&
      tEN.isOriginalTitle === true
    ) {
      translationEN.isOriginalTitle = false;
    } else if (
      this.form.value["originalTitleLocale"] === "en" &&
      tEN.isOriginalTitle === false
    ) {
      translationEN.isOriginalTitle = true;
    }

    const translations: ExhibitionTranslation[] = [
      translationCS,
      translationEN,
    ];
    this.dialogRef.close({ exhibition, translations });
  }

  /* ---------------------------------------------------------
  DIALOG CONTENT METHODS
  --------------------------------------------------------- */
  isCreateMode() {
    return this.mode === "create";
  }

  isUpdateMode() {
    return this.mode === "update";
  }

  isEditorContext() {
    return this.context === "editor";
  }

  isInventoryContext() {
    return this.context === "inventory";
  }

  canEdit() {
    const isLocalExhibition = this.defaults.exhibition?.inventoryId;
    return (
      this.isEditorContext() ||
      (this.isUpdateMode() && isLocalExhibition && this.isInventoryContext()) ||
      this.isCreateMode()
    );
  }

  private prepareForm() {
    const translationCS = this.exhibition.translations?.find(
      (a) => a.localeId === 1
    );
    const translationEN = this.exhibition.translations?.find(
      (a) => a.localeId === 2
    );

    let originalTitleLocale: "other" | "cs" | "en" = "cs";

    if (translationCS?.isOriginalTitle === true) {
      originalTitleLocale = "cs";
    } else if (translationEN?.isOriginalTitle === true) {
      originalTitleLocale = "en";
    } else if (!translationCS && !translationEN) {
      originalTitleLocale = "cs";
    } else {
      originalTitleLocale = "other";
    }

    this.form = this.fb.group({
      originalTitle: [this.exhibition.originalTitle],
      originalTitleLocale: [originalTitleLocale],
      countryCode: [this.exhibition.countryCode],
      titleCS: [translationCS?.title || ""],
      titleEN: [translationEN?.title || ""],
      cityNameCS: [translationCS?.cityName || ""],
      cityNameEN: [translationEN?.cityName || ""],
      descriptionCS: [translationCS?.description || ""],
      descriptionEN: [translationEN?.description || ""],
      yearCS: [translationCS?.year || ""],
      yearEN: [translationEN?.year || ""],
      year: [this.exhibition.year],
      organizerId: [
        this.exhibition.organizerId,
        { validators: Validators.required },
      ],
      curated: [this.exhibition.curated ?? false],
    });

    this.toggleDisabledOriginalTitle(
      this.form.get("originalTitleLocale").value
    );

    this.addModeBasedControls();
    this.subscribeToOriginalTitleLocale();
    this.subscribeToTitles();
  }

  private addModeBasedControls() {
    if (this.isCreateMode()) {
    }
    if (this.isUpdateMode()) {
    }
  }

  private subscribeToOriginalTitleLocale() {
    this.originalTitleLocaleSubscription = this.form
      .get("originalTitleLocale")
      .valueChanges.subscribe((value) => {
        this.toggleDisabledOriginalTitle(value);
      });
  }

  private toggleDisabledOriginalTitle(originalTitleLocaleValue: string) {
    if (originalTitleLocaleValue === "other") {
      this.form.get("originalTitle").enable();
    } else {
      this.form.get("originalTitle").disable();
    }
    this.setOriginalTitle();
    this.toggleRequiredTitle(originalTitleLocaleValue);
  }

  private subscribeToTitles() {
    this.titleEnSubscription = this.form
      .get("titleEN")
      .valueChanges.subscribe(() => {
        this.setOriginalTitle();
      });
    this.titleCsSubscription = this.form
      .get("titleCS")
      .valueChanges.subscribe(() => {
        this.setOriginalTitle();
      });
  }

  private setOriginalTitle() {
    switch (this.form.get("originalTitleLocale").value) {
      case "cs":
        this.form.get("originalTitle").setValue(this.form.get("titleCS").value);
        break;
      case "en":
        this.form.get("originalTitle").setValue(this.form.get("titleEN").value);
        break;
      case "other":
        this.form
          .get("originalTitle")
          .setValue(this.exhibition.originalTitle || "");
        break;
      default:
        break;
    }
  }

  private toggleRequiredTitle(originalTitleLocaleValue: string) {
    this.TITLE_CONTROL_NAMES.map((title) =>
      this.form.get(title).clearValidators()
    );
    switch (originalTitleLocaleValue) {
      case "cs":
        this.form.get("titleCS").setValidators(Validators.required);

        break;
      case "en":
        this.form.get("titleEN").setValidators(Validators.required);

        break;
      case "other":
        this.form.get("originalTitle").setValidators(Validators.required);

        break;
      default:
        break;
    }

    this.TITLE_CONTROL_NAMES.map((title) =>
      this.form.get(title).updateValueAndValidity()
    );
  }

  private getLocalizedOriginalTitle(): string {
    let originalTitle = this.form.value["originalTitle"];
    switch (this.form.value["originalTitleLocale"]) {
      case "cs":
        originalTitle = this.form.value["titleCS"];
        break;
      case "en":
        originalTitle = this.form.value["titleEN"];
        break;
      default:
        break;
    }
    return originalTitle;
  }

  getLocaleByCode(code: string): AvailableLocale {
    return this.availableLocales.find((locale) => locale.code === code);
  }
}
